/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import Button from '@react/react-spectrum/Button';
import ChevronLeft from '@react/react-spectrum/Icon/ChevronLeft';
import ChevronRight from '@react/react-spectrum/Icon/ChevronRight';
import React, {useCallback, useState, useEffect, useRef} from 'react';
import './ScrollCarousel.scss';
import Carousel from '@cchome/carousel';
import PropTypes from 'prop-types';

const propTypes = {
	children: PropTypes.element.isRequired,
	scrollDistance: PropTypes.number,
	fadeColor: PropTypes.string,
};

type ScrollCarouselProps = PropTypes.InferProps<typeof propTypes>;

const ScrollCarousel: React.FC<ScrollCarouselProps> = ({
	children,
	scrollDistance = 100,
	fadeColor = 'rgb(250, 250, 250)',
}) => {
	const [nodeRef, setNodeRef] = useState<HTMLSpanElement | null>(null);

	const [scrolledLeft, setScrolledLeft] = useState(true);
	const [scrolledRight, setScrolledRight] = useState(false);

	const scrollCarouselRef = useRef(null);

	const calculateArrows = (element) => {
		const {clientWidth, scrollWidth, scrollLeft} = element;

		if (scrollWidth - scrollLeft - clientWidth < 1) {
			setScrolledRight(true);
		}
		else {
			setScrolledRight(false);
		}

		if (scrollLeft < 1) {
			setScrolledLeft(true);
		}
		else {
			setScrolledLeft(false);
		}
	};

	const scrollElementRef = useCallback<(node: HTMLSpanElement | null) => void>((node) => {
		if (node !== null) {
			setNodeRef(node);
			calculateArrows(node);
		}
	}, []);

	const handleResize = () => {
		if (nodeRef !== null) {
			calculateArrows(nodeRef);
		}
	};

	useEffect(() => {
		window.addEventListener('resize', handleResize);
		return () => {
			window.removeEventListener('resize', handleResize);
		};
	});

	useEffect(() => {
		handleResize();
	}, [handleResize, scrollCarouselRef]);

	const handleScroll = (e) => {
		calculateArrows(e.target);
	};

	const handleScrollClick = (reverse = false) => (e) => {
		e.preventDefault();
		e.stopPropagation();
		if (nodeRef !== null && scrollDistance !== null) {
			nodeRef.scrollBy({
				left: reverse ? -1 * scrollDistance : scrollDistance,
			});
		}
	};

	const leftArrowStyle: React.CSSProperties = {
		boxShadow: `inset 100px 0 45px -50px ${fadeColor}`,
	};

	const rightArrowStyle: React.CSSProperties = {
		boxShadow: `inset -100px 0 45px -50px ${fadeColor}`,
	};

	return window.cchome.util.hasFeature('apphome_new_carousel') ? (
		<div className='scroll-carousel' ref={scrollCarouselRef}>
			<span
				className='scroll-carousel-body'
				ref={scrollElementRef}
				style={{
					scrollbarWidth: 'none', // this is here because the scss linter doesn't like the `scrollbar-width` property and won't pass the CI checks if I leave it
				}}
				onScroll={handleScroll}
			>
				<span className='scroll-carousel-spacer' />
				{children}
				<span className='scroll-carousel-spacer' />
			</span>
			<span
				className={`scroll-carousel-arrow scroll-carousel-arrow-left${scrolledLeft ? ' hidden' : ''}`}
				style={leftArrowStyle}
				onClick={handleScrollClick(true)}
			>
				<Button element='button' aria-label='Display previous results' label={<ChevronLeft />} variant='action' />
			</span>
			<span
				className={`scroll-carousel-arrow scroll-carousel-arrow-right${scrolledRight ? ' hidden' : ''}`}
				style={rightArrowStyle}
				onClick={handleScrollClick()}
			>
				<Button element='button' aria-label='Display next results' label={<ChevronRight />} variant='action' />
			</span>
		</div>
	) : (
		<Carousel>{children}</Carousel>
	);
};

ScrollCarousel.propTypes = propTypes;

export default ScrollCarousel;
export type {ScrollCarouselProps};
